// C&C Fraud Watch (Rachael Nelson, Oct 20, 2026).
// Flag a Crab & Cruise customer account as fraud (one at a time from a search, or a pasted list),
// see the alerts that went out, and set who gets them. The check itself runs every 15 minutes
// (netlify/functions/cnc-fraud-watch.js); the rules are in netlify/functions/lib/cnc-fraud-match.js.
// Staff cannot sign in to CNCx, which is why this lives in CPx.
import React, { useState, useEffect, useCallback } from 'react';
import { Loader, Search, ShieldAlert, X, RefreshCw } from 'lucide-react';
import { neonQuery } from '../services/api';

const DASH = String.fromCharCode(8212);
const day = (v) => {
  if (!v) return DASH;
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
};
const money = (n) => (n == null || n === '' ? DASH : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const name = (f, l) => [f, l].map((s) => String(s || '').trim()).filter(Boolean).join(' ');
const SOURCE = { manual: 'Flagged in CPx', import: 'Pasted list', 'order-note': 'Order note', chargeback: 'Chargebacks log' };

const LABEL = { fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase' };
const TH = { textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', whiteSpace: 'nowrap' };
const TD = { padding: '7px 8px', verticalAlign: 'top' };
const CARD = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 14 };
const SUB = { fontSize: 10.5, color: 'var(--gray-500)' };

export default function CncFraud() {
  const [tab, setTab] = useState('flags');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCleared, setShowCleared] = useState(false);

  const say = (t) => { setToast(t); setTimeout(() => setToast(null), 6000); };
  const load = useCallback(async (sc) => {
    setLoading(true); setErr(null);
    try { setData(await neonQuery('cnc_fraud_page', { showCleared: !!sc })); } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(showCleared); }, [load, showCleared]);

  const call = async (query, params, okMsg) => {
    setErr(null);
    try {
      const r = await neonQuery(query, params);
      if (r && r.ok === false) { setErr(r.reason || 'Not saved.'); return null; }
      if (okMsg) say(typeof okMsg === 'function' ? okMsg(r) : okMsg);
      load(showCleared);
      return r;
    } catch (e) { setErr(e.message); return null; }
  };

  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} className={tab === id ? 'btn btn-primary' : 'btn'} style={{ fontSize: 11.5 }}>{label}</button>
  );
  const flags = (data && data.flags) || [];
  const active = flags.filter((f) => !f.cleared_at).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>C&amp;C Fraud Watch</h2>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
            Crab &amp; Cruise accounts flagged as fraud. An email goes out when one buys tickets, and when a new account
            shares its name, billing address or a similar email. Checked every 15 minutes.
            {data ? ` ${active} flagged.` : ''}
          </div>
        </div>
        {tabBtn('flags', 'Flagged accounts')}
        {tabBtn('add', 'Flag accounts')}
        {tabBtn('alerts', 'Alerts')}
        {tabBtn('settings', 'Settings')}
      </div>

      {toast && <div style={{ marginBottom: 10, padding: '8px 12px', background: '#e7f6ec', color: '#1e7b3a', borderRadius: 6, fontSize: 12 }}>{toast}</div>}
      {err && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fdecea', color: '#b42318', borderRadius: 6, fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>{err}</span>
          <button onClick={() => setErr(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit' }}><X size={13} /></button>
        </div>
      )}
      {loading && !data && <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={16} className="spin" /> Loading</div>}

      {data && tab === 'flags' && (
        <FlagList flags={flags} showCleared={showCleared} setShowCleared={setShowCleared}
          onClear={(f) => {
            const note = window.prompt('Clear the fraud flag on ' + (name(f.first_name, f.last_name) || f.email) + '? Why?', '');
            if (note === null) return;
            call('cnc_fraud_clear', { id: f.id, note }, 'Flag cleared.');
          }} />
      )}
      {data && tab === 'add' && <AddFlags call={call} />}
      {data && tab === 'alerts' && <Alerts alerts={data.alerts || []} />}
      {data && tab === 'settings' && <Settings data={data} call={call} />}
    </div>
  );
}

function FlagList({ flags, showCleared, setShowCleared, onClear }) {
  return (
    <div style={CARD}>
      <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'flex-end' }}>
        <label style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>
          <input type="checkbox" checked={showCleared} onChange={(e) => setShowCleared(e.target.checked)} style={{ marginRight: 6 }} />
          Show cleared flags
        </label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: 'var(--gray-50)' }}>
            <th style={TH}>Customer</th><th style={TH}>Phone</th><th style={TH}>Billing</th><th style={TH}>C&amp;C orders</th>
            <th style={TH}>Why</th><th style={TH}>Flagged</th><th style={TH} />
          </tr></thead>
          <tbody>
            {flags.length === 0 && <tr><td colSpan={7} style={{ ...TD, color: 'var(--gray-500)', padding: 20, textAlign: 'center' }}>No flagged accounts.</td></tr>}
            {flags.map((f, i) => (
              <tr key={f.id} style={{ borderTop: '1px solid var(--gray-200)', background: i % 2 ? 'var(--gray-50)' : '#fff', opacity: f.cleared_at ? 0.55 : 1 }}>
                <td style={TD}>
                  <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{name(f.first_name, f.last_name) || DASH}</div>
                  {f.email && <div style={SUB}>{f.email}</div>}
                  {!f.user_id && <div style={{ ...SUB, color: '#b54708' }}>No C&amp;C account found yet</div>}
                </td>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>{f.phone || DASH}</td>
                <td style={TD}>{f.bill_address ? <>{f.bill_address}<div style={SUB}>{[f.bill_city, f.bill_state, f.bill_zip].filter(Boolean).join(', ')}</div></> : DASH}</td>
                <td style={{ ...TD, textAlign: 'center' }}>{f.orders || 0}</td>
                <td style={{ ...TD, maxWidth: 320 }}>{f.reason || DASH}<div style={SUB}>{SOURCE[f.source] || f.source}</div></td>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                  {day(f.flagged_at)}<div style={SUB}>{f.flagged_by || ''}</div>
                  {f.cleared_at && <div style={{ ...SUB, color: '#1e7b3a' }}>Cleared {day(f.cleared_at)} by {f.cleared_by}{f.cleared_note ? ': ' + f.cleared_note : ''}</div>}
                </td>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                  {!f.cleared_at && <button className="btn" style={{ fontSize: 11 }} onClick={() => onClear(f)}>Clear flag</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddFlags({ call }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [text, setText] = useState('');
  const [reason, setReason] = useState('2024 chargeback list');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const search = async () => {
    if (q.trim().length < 3) return;
    setSearching(true);
    try { setResults((await neonQuery('cnc_fraud_search', { q })).accounts || []); } catch (e) { setResults([]); }
    setSearching(false);
  };
  const flagOne = async (a) => {
    const why = window.prompt('Flag ' + (name(a.first_name, a.last_name) || a.email) + ' as fraud. Why?', 'Fraud chargeback');
    if (why === null) return;
    const r = await call('cnc_fraud_flag', { userId: a.user_id, email: a.email, reason: why }, 'Flagged as fraud.');
    if (r) setResults((rs) => (rs || []).map((x) => (x.user_id === a.user_id ? { ...x, flag_id: r.id } : x)));
  };
  const runImport = async () => {
    if (!text.trim()) return;
    setImporting(true); setImportResult(null);
    const r = await call('cnc_fraud_import', { text, reason }, (x) => `Flagged ${x.flagged} account${x.flagged === 1 ? '' : 's'}.`);
    if (r) setImportResult(r);
    setImporting(false);
  };

  return (
    <>
      <div style={{ ...CARD, padding: 14 }}>
        <div style={LABEL}>Find a C&amp;C customer</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" style={{ flex: 1, fontSize: 12 }} placeholder="Email, name, phone, order id or transaction #"
            value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
          <button className="btn btn-navy" style={{ fontSize: 11.5 }} onClick={search} disabled={searching}>
            {searching ? <Loader size={12} className="spin" /> : <Search size={12} style={{ verticalAlign: -2 }} />} Search
          </button>
        </div>
        {results && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
            <thead><tr style={{ background: 'var(--gray-50)' }}>
              <th style={TH}>Customer</th><th style={TH}>Phone</th><th style={TH}>Billing</th><th style={TH}>Paid orders</th><th style={TH}>Last order</th><th style={TH}>Is fraud</th>
            </tr></thead>
            <tbody>
              {results.length === 0 && <tr><td colSpan={6} style={{ ...TD, color: 'var(--gray-500)' }}>No C&amp;C accounts match.</td></tr>}
              {results.map((a) => (
                <tr key={a.user_id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                  <td style={TD}><div style={{ fontWeight: 600 }}>{name(a.first_name, a.last_name) || DASH}</div><div style={SUB}>{a.email}</div></td>
                  <td style={TD}>{a.phone || DASH}</td>
                  <td style={TD}>{[a.city, a.state].filter(Boolean).join(', ') || DASH}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>{a.orders}</td>
                  <td style={TD}>{day(a.last_order)}</td>
                  <td style={TD}>
                    <label style={{ cursor: a.flag_id ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, color: a.flag_id ? '#b42318' : 'inherit', fontWeight: a.flag_id ? 700 : 400 }}>
                      <input type="checkbox" checked={!!a.flag_id} disabled={!!a.flag_id} onChange={() => flagOne(a)} />
                      {a.flag_id ? 'Flagged' : 'Is Fraud'}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ ...CARD, padding: 14 }}>
        <div style={LABEL}>Flag a list</div>
        <div style={{ fontSize: 11.5, color: 'var(--gray-600)', marginBottom: 8 }}>
          Paste one customer per line: an email, a name, or rows copied straight from the spreadsheet. An email or name
          that has never bought from C&amp;C is still flagged, so its first purchase is caught. A name that matches more than
          one C&amp;C account is not flagged; search for it above and pick the right one.
        </div>
        <textarea className="form-input" style={{ width: '100%', minHeight: 140, fontSize: 12, boxSizing: 'border-box', fontFamily: 'inherit' }}
          placeholder={'angwebb84@gmail.com\nMary Tucker\nWebb, Angela'} value={text} onChange={(e) => setText(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>Reason</span>
          <input className="form-input" style={{ flex: 1, fontSize: 12 }} value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="btn btn-navy" style={{ fontSize: 11.5 }} onClick={runImport} disabled={importing || !text.trim()}>
            {importing ? <Loader size={12} className="spin" /> : <ShieldAlert size={12} style={{ verticalAlign: -2 }} />} Flag these
          </button>
        </div>
        {importResult && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-700)' }}>
            {importResult.lines} lines read. {importResult.flagged} flagged
            {importResult.notInCnc ? ` (${importResult.notInCnc} not found in C&C orders, flagged by email or name so a first purchase is caught)` : ''}.
            {importResult.alreadyFlagged ? ` ${importResult.alreadyFlagged} were already flagged.` : ''}
            {importResult.ambiguous && importResult.ambiguous.length > 0 && (
              <div style={{ marginTop: 6, color: '#b54708' }}>
                Not flagged, more than one account with that name:{' '}
                {importResult.ambiguous.map((a) => `${a.line} (${a.accounts})`).join('; ')}
              </div>
            )}
            {importResult.skipped && importResult.skipped.length > 0 && (
              <div style={{ marginTop: 6, color: 'var(--gray-500)' }}>Skipped, no email or full name: {importResult.skipped.join('; ')}</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Alerts({ alerts }) {
  return (
    <div style={CARD}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: 'var(--gray-50)' }}>
            <th style={TH}>When</th><th style={TH}>Alert</th><th style={TH}>Buyer</th><th style={TH}>Order</th><th style={TH}>Flagged account</th><th style={TH}>Matched on</th>
          </tr></thead>
          <tbody>
            {alerts.length === 0 && <tr><td colSpan={6} style={{ ...TD, color: 'var(--gray-500)', padding: 20, textAlign: 'center' }}>No alerts yet.</td></tr>}
            {alerts.map((a, i) => (
              <tr key={a.id} style={{ borderTop: '1px solid var(--gray-200)', background: i % 2 ? 'var(--gray-50)' : '#fff' }}>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>{day(a.created_at)}{!a.emailed_at && <div style={{ ...SUB, color: '#b54708' }}>not emailed</div>}</td>
                <td style={TD}>{a.kind === 'purchase'
                  ? <span style={{ color: '#b42318', fontWeight: 700 }}>Bought tickets</span>
                  : <span style={{ color: '#b54708', fontWeight: 700 }}>New look-alike account</span>}</td>
                <td style={TD}>{a.buyer || DASH}{a.buyer_email && <div style={SUB}>{a.buyer_email}</div>}</td>
                <td style={TD}>{a.order_id || DASH}{a.trans_id && <div style={SUB}>transaction {a.trans_id}</div>}{a.total != null && <div style={SUB}>{money(a.total)}</div>}</td>
                <td style={TD}>{name(a.first_name, a.last_name) || DASH}{a.flag_email && <div style={SUB}>{a.flag_email}</div>}</td>
                <td style={TD}>{(a.reasons || []).map((r, j) => <div key={j}>{r.text}</div>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Settings({ data, call }) {
  const [rcpt, setRcpt] = useState(data.recipients || '');
  const [running, setRunning] = useState(false);
  const runNow = async () => {
    setRunning(true);
    await call('cnc_fraud_run', {}, (r) => `Checked ${r.ordersChecked} recent orders and ${r.accountsChecked} recent accounts. ${r.purchaseAlerts + r.newAccountAlerts} alert email${r.purchaseAlerts + r.newAccountAlerts === 1 ? '' : 's'} sent.`);
    setRunning(false);
  };
  return (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={LABEL}>Who gets the alert emails</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-input" style={{ flex: 1, fontSize: 12 }} value={rcpt} onChange={(e) => setRcpt(e.target.value)} />
        <button className="btn btn-navy" style={{ fontSize: 11.5 }} onClick={() => call('cnc_fraud_recipients_set', { recipients: rcpt }, 'Saved.')}>Save</button>
      </div>
      <div style={{ ...SUB, marginTop: 4 }}>Separate addresses with commas.</div>

      <div style={{ ...LABEL, marginTop: 18 }}>How matching works</div>
      <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.55 }}>
        <div><b>Bought tickets</b> (cancel it): a paid order on the same C&amp;C account, or with the same email, the same billing address and ZIP, or the same phone as any order on a flagged account.</div>
        <div style={{ marginTop: 4 }}><b>New look-alike account</b> (look at it): a C&amp;C account we have not seen before with the same first and last name, the same billing address, or an email that reads like a flagged one (the same letters before the @ on another site, the same letters with different numbers, or built from the flagged name).</div>
        <div style={{ marginTop: 4 }}>Each order and each account is emailed about once. The feast venue address and the office phone numbers never count as a match.</div>
        <div style={{ marginTop: 4, color: 'var(--gray-500)' }}>A stolen card's billing address belongs to the cardholder, so an address match can point at the victim. Check before cancelling.</div>
      </div>

      <div style={{ ...LABEL, marginTop: 18 }}>Run the check now</div>
      <button className="btn" style={{ fontSize: 11.5 }} onClick={runNow} disabled={running}>
        {running ? <Loader size={12} className="spin" /> : <RefreshCw size={12} style={{ verticalAlign: -2 }} />} Check recent orders
      </button>
      <div style={{ ...SUB, marginTop: 4 }}>
        It also runs by itself every 15 minutes.
        {data.baselineAt ? ` Watching orders placed since ${day(data.baselineAt)}.` : ''}
        {data.lastSeedAt ? ` Fraud chargebacks in order notes last picked up ${day(data.lastSeedAt)}.` : ''}
      </div>
    </div>
  );
}
